import {
    useAccountBalance,
    useAccountKey,
    useAccountProfile,
    useAuthActions,
    useAuthState,
} from "@pollinations/sdk/react";
import { useEffect } from "react";
import { AccountMenu } from "../../compositions/AccountMenu.tsx";
import { cn } from "../../lib/cn.ts";
import { BrandMark } from "../../primitives/BrandMark.tsx";
import { DropdownItem } from "../../primitives/DropdownItem.tsx";
import {
    AppIcon,
    BugIcon,
    DiscordIcon,
    ExternalLinkIcon,
    KeyIcon,
    PowerIcon,
    WalletIcon,
} from "../../primitives/icons/index.tsx";
import { LoginButton } from "../auth/sdk.ts";
import {
    AccountPollen,
    type AccountPollenSource,
} from "../wallet/AccountPollen.tsx";

export type AppUserMenuLabels = {
    authorize: string;
    appUserMenu: string;
    permissions: string;
    buyPollen: string;
    logout: string;
};

export const MENU_LINK_ICONS = {
    app: AppIcon,
    discord: DiscordIcon,
    bug: BugIcon,
};

export type AppUserMenuProps = {
    /** Optional caller-owned dashboard destination for the linked avatar. */
    dashboardHref?: string;
    /** Larger logged-out action for prominent placements such as a page hero. */
    connectSize?: keyof typeof CONNECT_SIZES;
    labels?: Partial<AppUserMenuLabels>;
    /** Class for the dropdown panel, which renders in a portal outside the menu. */
    menuClassName?: string;
    menuLinks?: {
        label: string;
        href: string;
        icon?: keyof typeof MENU_LINK_ICONS;
    }[];
    showBuyPollen?: boolean;
};

const defaultLabels: AppUserMenuLabels = {
    authorize: "Pollinations Connect",
    appUserMenu: "App user menu",
    permissions: "Permissions",
    buyPollen: "Buy Pollen",
    logout: "Disconnect",
};

const CONNECT_SIZES = {
    md: {
        button: "polli:h-10 polli:px-4",
        mark: "polli:h-5 polli:w-5",
    },
    lg: {
        button: "polli:h-12 polli:px-5 polli:text-base",
        mark: "polli:h-6 polli:w-6",
    },
};

export function AppUserMenu({
    dashboardHref,
    connectSize = "md",
    labels: labelOverrides,
    menuClassName,
    menuLinks = [],
    showBuyPollen = true,
}: AppUserMenuProps) {
    const labels = { ...defaultLabels, ...labelOverrides };
    const size = CONNECT_SIZES[connectSize];
    const { logout, enterUrl } = useAuthActions();
    const { isLoggedIn } = useAuthState();
    const profile = useAccountProfile({ enabled: isLoggedIn });
    const key = useAccountKey({ enabled: isLoggedIn });
    // The wallet is shown only for an unlimited key that may read it; ask only then.
    const canReadWallet =
        key.data?.pollenBudget === null &&
        (key.data.permissions?.account?.includes("usage") ?? false);
    const balance = useAccountBalance({
        enabled: isLoggedIn && canReadWallet,
    });
    const wallet = balance.data?.accountBalance;
    useEffect(() => {
        if (!isLoggedIn) return;
        const onVisible = () => {
            if (document.visibilityState !== "visible") return;
            void key.refresh();
            void balance.refresh();
        };
        document.addEventListener("visibilitychange", onVisible);
        return () =>
            document.removeEventListener("visibilitychange", onVisible);
    }, [isLoggedIn, key.refresh, balance.refresh]);
    const returnUrl =
        typeof window === "undefined" ? undefined : window.location.href;
    const topUpUrl = new URL("/top-up", enterUrl);
    if (returnUrl) topUpUrl.searchParams.set("redirect", returnUrl);
    const keyId = key.data?.id;
    let editKeyUrl: URL | undefined;
    if (keyId) {
        editKeyUrl = new URL("/edit-key", enterUrl);
        editKeyUrl.searchParams.set("id", keyId);
        if (returnUrl) editKeyUrl.searchParams.set("redirect", returnUrl);
    }
    let pollenSource: AccountPollenSource | undefined;
    if (key.data && key.data.pollenBudget !== null) {
        pollenSource = {
            type: "budget",
            remaining: key.data.pollenBudget,
            generationEnabled: key.data.permissions?.models?.length !== 0,
        };
    } else if (wallet) {
        pollenSource = {
            type: "wallet",
            balances: { paid: wallet.paid, quest: wallet.tier },
        };
    } else if (key.data && !canReadWallet) {
        pollenSource = { type: "budget", remaining: null };
    }

    return (
        <div
            data-theme="accent"
            className="polli:flex polli:shrink-0 polli:justify-end"
        >
            {!isLoggedIn ? (
                <LoginButton
                    intent="brand"
                    className={cn(
                        "polli:gap-2 polli:py-0 polli:whitespace-nowrap",
                        size.button,
                    )}
                >
                    <BrandMark className={size.mark} />
                    {labels.authorize}
                </LoginButton>
            ) : (
                <AccountMenu
                    name={
                        profile.data?.name ||
                        profile.data?.githubUsername ||
                        "Connected user"
                    }
                    avatarUrl={profile.data?.image}
                    dashboardHref={
                        dashboardHref ?? new URL("/pollen", enterUrl).href
                    }
                    menuLabel={labels.appUserMenu}
                    className="polli:max-w-80"
                    menuClassName={menuClassName}
                    secondaryContent={
                        pollenSource ? (
                            <AccountPollen source={pollenSource} />
                        ) : undefined
                    }
                >
                    {(close) => (
                        <>
                            {menuLinks.map(({ label, href, icon }) => {
                                const Icon = icon
                                    ? MENU_LINK_ICONS[icon]
                                    : ExternalLinkIcon;
                                return (
                                    <DropdownItem
                                        key={href}
                                        as="a"
                                        href={href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={close}
                                    >
                                        <Icon
                                            className="polli:h-4 polli:w-4 polli:shrink-0"
                                            aria-hidden="true"
                                        />
                                        {label}
                                        <ExternalLinkIcon
                                            className="polli:ml-auto polli:h-3.5 polli:w-3.5 polli:shrink-0"
                                            aria-hidden="true"
                                        />
                                    </DropdownItem>
                                );
                            })}
                            {menuLinks.length > 0 && (
                                <hr className="polli:my-1 polli:border-t polli:border-theme-text-strong/15" />
                            )}
                            {editKeyUrl && (
                                <DropdownItem
                                    as="a"
                                    href={editKeyUrl.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={close}
                                >
                                    <KeyIcon
                                        className="polli:h-4 polli:w-4 polli:shrink-0"
                                        aria-hidden="true"
                                    />
                                    {labels.permissions}
                                    <ExternalLinkIcon
                                        className="polli:ml-auto polli:h-3.5 polli:w-3.5 polli:shrink-0"
                                        aria-hidden="true"
                                    />
                                </DropdownItem>
                            )}
                            {showBuyPollen && (
                                <DropdownItem
                                    as="a"
                                    href={topUpUrl.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={close}
                                >
                                    <WalletIcon
                                        className="polli:h-4 polli:w-4 polli:shrink-0"
                                        aria-hidden="true"
                                    />
                                    {labels.buyPollen}
                                    <ExternalLinkIcon
                                        className="polli:ml-auto polli:h-3.5 polli:w-3.5 polli:shrink-0"
                                        aria-hidden="true"
                                    />
                                </DropdownItem>
                            )}
                            <DropdownItem
                                type="button"
                                className="polli:justify-start polli:text-left"
                                onClick={() => {
                                    close();
                                    logout();
                                }}
                            >
                                <PowerIcon
                                    className="polli:h-4 polli:w-4 polli:shrink-0"
                                    aria-hidden="true"
                                />
                                {labels.logout}
                            </DropdownItem>
                        </>
                    )}
                </AccountMenu>
            )}
        </div>
    );
}
